import { useEffect, useState, useMemo } from "react";
import { Sidebar } from "../../components/sidebar";
import { Wrapper } from "../../components/wrapper";
import { findAllCars, deleteCar } from "../../services/car";
import AlertDialog from "../../components/alert-dialog";
import { formatNumberAsThousands } from "../../utils";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { Spinner } from "../../components/spinner";

const ITEMS_PER_PAGE = 10;

const debounce = (fn, delay) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

export const History = () => {
  const navigate = useNavigate();
  const [record, setRecords] = useState([]);
  const [search, updateSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });
  const [filters, setFilters] = useState({
    brand: "",
    motorType: "",
    year: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    findAllCars()
      .then((data) => {
        const reversedData = data.reverse();
        setRecords(reversedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const availableFilters = useMemo(() => {
    const brands = [...new Set(record.map((r) => r.brand).filter(Boolean))].sort();
    const motorTypes = [...new Set(record.map((r) => r.motor_type).filter(Boolean))].sort();
    const years = [...new Set(record.map((r) => r.year).filter(Boolean))].sort((a, b) => b - a);

    return { brands, motorTypes, years };
  }, [record]);

  const filteredRecords = useMemo(() => {
    let filtered = record;

    if (search) {
      const lowerCaseSearch = search.toLowerCase();
      filtered = filtered.filter((record) =>
        Object.values(record).some((value) =>
          String(value).toLowerCase().includes(lowerCaseSearch)
        )
      );
    }

    if (filters.brand) {
      filtered = filtered.filter((r) => r.brand === filters.brand);
    }
    if (filters.motorType) {
      filtered = filtered.filter((r) => r.motor_type === filters.motorType);
    }
    if (filters.year) {
      filtered = filtered.filter((r) => String(r.year) === String(filters.year));
    }

    return filtered;
  }, [search, record, filters]);

  const sortedRecords = useMemo(() => {
    if (!sortConfig.key) return filteredRecords;

    const sorted = [...filteredRecords].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (sortConfig.key === "kms") {
        return sortConfig.direction === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      const aString = String(aValue || "").toLowerCase();
      const bString = String(bValue || "").toLowerCase();

      if (aString < bString) return sortConfig.direction === "asc" ? -1 : 1;
      if (aString > bString) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredRecords, sortConfig]);

  const totalPages = Math.ceil(sortedRecords.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentRecords = sortedRecords.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleDelete = (record) => {
    setPendingDelete(record);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setPendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!pendingDelete?._id) return;
    setDeleting(true);
    try {
      await deleteCar(pendingDelete._id);
      setRecords((prev) => prev.filter((r) => r._id !== pendingDelete._id));
      setPendingDelete(null);
    } catch (err) {
      console.error("Error eliminando vehículo:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      brand: "",
      motorType: "",
      year: "",
    });
    updateSearch("");
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.brand || filters.motorType || filters.year || search;

  return (
    <div className="flex w-full min-h-screen">
      <Sidebar />
      <div className="flex-1 w-full p-4 bg-sidebar-bg">
        <Wrapper>
          <div className="w-full max-w-7xl mx-auto px-4 lg:px-6 py-8">
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <DirectionsCarIcon
                      className="text-blue-400"
                      sx={{ fontSize: 28 }}
                    />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-white">
                      Historial de Registros
                    </h1>
                    <p className="text-gray-400 text-sm mt-1">
                      {sortedRecords.length} registros • Página {currentPage} de{" "}
                      {totalPages}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => navigate("/dashboard/newfile")}
                  className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/20 group"
                >
                  <AddIcon className="group-hover:rotate-90 transition-transform duration-200" />
                  Nuevo Registro
                </button>
              </div>
            </div>

            <div className="mb-6 space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <SearchIcon className="text-gray-500" sx={{ fontSize: 20 }} />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => updateSearch(e.target.value)}
                  placeholder="Buscar por patente, nombre, RUT..."
                  className="w-full pl-12 pr-4 py-3 bg-sidebar-bg border border-sidebar-accent rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="bg-sidebar-accent/30 rounded-lg border border-sidebar-accent overflow-hidden">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full px-4 py-3 flex items-center justify-between text-white hover:bg-sidebar-accent/50 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <FilterListIcon sx={{ fontSize: 20 }} />
                    <span className="font-medium">Filtros Avanzados</span>
                    {hasActiveFilters && (
                      <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        {[filters.brand, filters.motorType, filters.year, search].filter(Boolean).length}
                      </span>
                    )}
                  </div>
                  <ExpandMoreIcon
                    className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
                    sx={{ fontSize: 20 }}
                  />
                </button>

                {showFilters && (
                  <div className="p-4 bg-sidebar-bg/50 border-t border-sidebar-accent">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <FilterSelect
                        label="Marca"
                        value={filters.brand}
                        onChange={(value) => handleFilterChange("brand", value)}
                        options={availableFilters.brands}
                        placeholder="Todas las marcas"
                      />
                      <FilterSelect
                        label="Tipo de Motor"
                        value={filters.motorType}
                        onChange={(value) => handleFilterChange("motorType", value)}
                        options={availableFilters.motorTypes}
                        placeholder="Todos los tipos"
                      />
                      <FilterSelect
                        label="Año"
                        value={filters.year}
                        onChange={(value) => handleFilterChange("year", value)}
                        options={availableFilters.years}
                        placeholder="Todos los años"
                      />
                    </div>

                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-medium rounded-lg transition-all border border-red-500/20"
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                        Limpiar Filtros
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <Spinner />
            ) : sortedRecords.length === 0 ? (
              <EmptyState search={search} />
            ) : (
              <>
                <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent overflow-hidden">
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-sidebar-accent border-b border-sidebar-accent">
                        <tr>
                          <SortableHeader
                            label="Patente"
                            sortKey="license"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            label="Cliente"
                            sortKey="customer_name"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            label="Tipo"
                            sortKey="brand"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            label="Estado"
                            sortKey="status"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            label="Teléfono"
                            sortKey="phone"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            label="KMs"
                            sortKey="kms"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <SortableHeader
                            label="Fecha"
                            sortKey="date"
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                          <TableHeader>Acciones</TableHeader>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-sidebar-accent">
                        {currentRecords.map((record, idx) => (
                          <tr
                            key={record._id || idx}
                            className="hover:bg-black transition-colors"
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center border border-blue-500/20">
                                  <DirectionsCarIcon
                                    className="text-blue-400"
                                    sx={{ fontSize: 16 }}
                                  />
                                </div>
                                <span className="font-semibold text-white uppercase">
                                  {record.license}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium text-white capitalize">
                                  {record.customer_name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {record.customer_id}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <TypeBadge type={record.brand} />
                                {record.motor_type && (
                                  <span className="text-xs text-gray-500">
                                    {record.motor_type}
                                  </span>
                                )}
                                {record.year && (
                                  <span className="text-xs text-gray-500">
                                    Año {record.year}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <StatusBadge status="active" />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <PhoneIcon
                                  className="text-gray-500"
                                  sx={{ fontSize: 14 }}
                                />
                                <span className="text-gray-400">
                                  {record.phone || "-"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-sm">
                                {formatNumberAsThousands(record.kms)} km
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CalendarMonthIcon
                                  className="text-gray-500"
                                  sx={{ fontSize: 14 }}
                                />
                                <span className="text-gray-400">
                                  {record.date}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() =>
                                    navigate(`/dashboard/newfile?${record._id}`)
                                  }
                                  className="p-2 hover:bg-blue-500/10 rounded-lg transition-all group border border-transparent hover:border-blue-500/20"
                                  title="Ver detalles"
                                >
                                  <VisibilityIcon
                                    className="text-gray-400 group-hover:text-blue-400 transition-colors"
                                    sx={{ fontSize: 18 }}
                                  />
                                </button>
                                <button
                                  onClick={() => handleDelete(record)}
                                  className="p-2 hover:bg-red-500/10 rounded-lg transition-all group border border-transparent hover:border-red-500/20"
                                  title="Eliminar"
                                >
                                  <DeleteIcon
                                    className="text-gray-400 group-hover:text-red-400 transition-colors"
                                    sx={{ fontSize: 18 }}
                                  />
                                </button>
                              </div>
                            </TableCell>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="lg:hidden space-y-4 p-4">
                    {currentRecords.map((record, idx) => (
                      <MobileCard
                        key={record._id || idx}
                        record={record}
                        onView={() =>
                          navigate(`/dashboard/newfile?${record._id}`)
                        }
                        onDelete={() => handleDelete(record)}
                      />
                    ))}
                  </div>
                </div>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalItems={sortedRecords.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                  />
                )}
              </>
            )}
          </div>
        </Wrapper>
      </div>
      <AlertDialog
        open={!!pendingDelete}
        title="¿Eliminar vehículo?"
        body={
          pendingDelete
            ? `Se eliminará el registro del vehículo con patente ${(pendingDelete.license || "").toUpperCase()} (${pendingDelete.customer_name || ""}). El cliente NO será eliminado. Esta acción no se puede deshacer.`
            : ""
        }
        handleClose={cancelDelete}
        handleConfirm={confirmDelete}
        ConfirmButtonText={deleting ? "Eliminando..." : "Eliminar"}
      />
    </div>
  );
};

const SortableHeader = ({ label, sortKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === sortKey;
  const direction = sortConfig.direction;

  return (
    <th
      onClick={() => onSort(sortKey)}
      className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-300 transition-colors group"
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        <div className="flex flex-col">
          {isActive ? (
            direction === "asc" ? (
              <ArrowUpwardIcon
                className="text-blue-400"
                sx={{ fontSize: 14 }}
              />
            ) : (
              <ArrowDownwardIcon
                className="text-blue-400"
                sx={{ fontSize: 14 }}
              />
            )
          ) : (
            <div className="flex flex-col opacity-20 group-hover:opacity-40 transition-opacity">
              <ArrowUpwardIcon sx={{ fontSize: 12 }} />
              <ArrowDownwardIcon sx={{ fontSize: 12, marginTop: -0.5 }} />
            </div>
          )}
        </div>
      </div>
    </th>
  );
};

const TableHeader = ({ children }) => (
  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
    {children}
  </th>
);

const TableCell = ({ children, className = "" }) => (
  <td className={`px-6 py-4 text-sm text-white bg-sidebar-bg ${className}`}>{children}</td>
);

const StatusBadge = ({ status = "active" }) => {
  const statusConfig = {
    active: {
      text: "Activo",
      className: "bg-green-500/10 text-green-400 border-green-500/20",
      icon: "●",
    },
    pending: {
      text: "Pendiente",
      className: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      icon: "●",
    },
    inactive: {
      text: "Inactivo",
      className: "bg-gray-500/10 text-gray-400 border-gray-500/20",
      icon: "●",
    },
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${config.className}`}
    >
      <span className="text-[8px]">{config.icon}</span>
      {config.text}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  return (
    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
      {type}
    </span>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-sidebar-bg border-t border-sidebar-accent">
      <div className="text-sm text-gray-400">
        <span className="font-medium text-white">{startItem}</span>-
        <span className="font-medium text-white">{endItem}</span> de{" "}
        <span className="font-medium text-white">{totalItems}</span> registros
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </button>

        <div className="flex gap-1">
          {getPageNumbers().map((page, idx) =>
            page === "..." ? (
              <span
                key={`ellipsis-${idx}`}
                className="px-3 py-2 text-gray-500 text-sm"
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`min-w-[36px] px-3 py-2 text-sm rounded-lg font-medium transition-all ${
                  currentPage === page
                    ? "bg-blue-600 text-white"
                    : "bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white"
                }`}
              >
                {page}
              </button>
            )
          )}
        </div>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg bg-sidebar-accent border border-sidebar-accent text-gray-400 hover:bg-sidebar-hover hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </button>
      </div>
    </div>
  );
};

const MobileCard = ({ record, onView, onDelete }) => (
  <div className="bg-sidebar-accent/30 rounded-lg p-4 space-y-4 border border-sidebar-accent">
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center border border-blue-500/20">
          <DirectionsCarIcon className="text-blue-400" sx={{ fontSize: 20 }} />
        </div>
        <div>
          <p className="text-base font-bold text-white uppercase">
            {record.license}
          </p>
          <p className="text-sm text-gray-400 capitalize">
            {record.brand} • {record.model}
          </p>
        </div>
      </div>
      <StatusBadge status="active" />
    </div>

    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-sidebar-accent">
      <MobileInfoItem
        icon={PersonIcon}
        label="Cliente"
        value={record.customer_name}
      />
      <MobileInfoItem icon={BadgeIcon} label="RUT" value={record.customer_id} />
      <MobileInfoItem
        icon={DirectionsCarIcon}
        label="KMs"
        value={`${formatNumberAsThousands(record.kms)} km`}
      />
      <MobileInfoItem
        icon={CalendarMonthIcon}
        label="Fecha"
        value={record.date}
      />
    </div>

    <div className="flex gap-2 pt-3 border-t border-sidebar-accent">
      <button
        onClick={onView}
        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2"
      >
        <VisibilityIcon sx={{ fontSize: 16 }} />
        Ver
      </button>
      <button
        onClick={onDelete}
        className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 border border-red-500/20"
      >
        <DeleteIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  </div>
);

const MobileInfoItem = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="text-gray-500" sx={{ fontSize: 14 }} />
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    </div>
    <p className="text-sm text-white font-medium capitalize truncate">
      {value}
    </p>
  </div>
);

const EmptyState = ({ search }) => (
  <div className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-12 text-center">
    <div className="flex flex-col items-center gap-4">
      <div className="p-4 bg-gray-700/30 rounded-full">
        <SearchIcon className="text-gray-500" sx={{ fontSize: 48 }} />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-400 mb-2">
          {search ? "No se encontraron resultados" : "No hay registros"}
        </h3>
        <p className="text-sm text-gray-500">
          {search
            ? `No hay registros que coincidan con "${search}"`
            : "Comienza creando tu primer registro"}
        </p>
      </div>
    </div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, placeholder }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 bg-sidebar-bg border border-sidebar-accent rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all appearance-none cursor-pointer hover:border-blue-500/50"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
        backgroundPosition: "right 0.5rem center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "1.5em 1.5em",
        paddingRight: "2.5rem",
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);
